import React from 'react';
import { createBrowserRouter, RouterProvider, Outlet, Navigate } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import FeaturesPage from './pages/FeaturesPage';
import PricingPage from './pages/PricingPage';
import AboutPage from './pages/AboutPage';
import DocsPage from './pages/DocsPage';
import LoginPage from './pages/LoginPage';
import NativeNotebookPage from './pages/NativeNotebookPage';
import ComputePage from './pages/ComputePage';
import BillingPage from './pages/BillingPage';
import SettingsPage from './pages/SettingsPage';
import SessionsPage from './pages/SessionsPage';
import StrategiesPage from './pages/StrategiesPage';
import StrategyDetailPage from './pages/StrategyDetailPage';
import StrategyLibraryPage from './pages/StrategyLibraryPage';
import ResearchFeedPage from './pages/ResearchFeedPage';
import SignalFeedsPage from './pages/SignalFeedsPage';
import ApiKeysPage from './pages/ApiKeysPage';
import StrategyComposerPage from './pages/StrategyComposerPage';
import LabPage from './pages/LabPage';
import NotebooksPage from './pages/NotebooksPage';
import DataPage from './pages/DataPage';
import StoragePage from './pages/StoragePage';
import CompetitionsPage from './pages/CompetitionsPage';
import CompetitionDetailPage from './pages/CompetitionDetailPage';
import LeaderboardPage from './pages/LeaderboardPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminResearchPage from './pages/AdminResearchPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminUserDetailsPage from './pages/AdminUserDetailsPage';
import AdminGpuQuotaDashboard from './pages/AdminGpuQuotaDashboard';
import AdminAMIBuilderPage from './pages/AdminAMIBuilderPage';
import CrossCloudCostMonitorPage from './pages/CrossCloudCostMonitorPage';
import AdminOrphanInstancesPage from './pages/AdminOrphanInstancesPage';
import AdminSampleNotebooksPage from './pages/AdminSampleNotebooksPage';
import AdminDatasetsPage from './pages/AdminDatasetsPage';
import TestCodeMirror from './pages/TestCodeMirror';
import StandaloneTerminalPage from './pages/StandaloneTerminalPage';
import StandaloneProxyPage from './pages/StandaloneProxyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import CompetitionRulesPage from './pages/CompetitionRulesPage';
import DashboardLayout from './components/DashboardLayout';
import RouteErrorBoundary from './components/GlobalErrorBoundary';
import { SidebarProvider } from './contexts/SidebarContext';
import { LaunchNotificationProvider } from './contexts/LaunchNotificationContext';
import { UserInstancesProvider } from './contexts/UserInstancesContext';
import { IdleNotificationProvider } from './contexts/IdleNotificationContext';
import './App.css';

function DashboardWrapper() {
  return (
    <SidebarProvider>
      <LaunchNotificationProvider>
        <UserInstancesProvider>
          <IdleNotificationProvider>
            <DashboardLayout>
              <Outlet />
            </DashboardLayout>
          </IdleNotificationProvider>
        </UserInstancesProvider>
      </LaunchNotificationProvider>
    </SidebarProvider>
  );
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <LandingPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/features',
    element: <FeaturesPage />,
  },
  {
    path: '/pricing',
    element: <PricingPage />,
  },
  {
    path: '/about',
    element: <AboutPage />,
  },
  {
    path: '/docs',
    element: <DocsPage />,
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/terms',
    element: <TermsOfServicePage />,
  },
  {
    path: '/privacy',
    element: <PrivacyPolicyPage />,
  },
  {
    path: '/rules',
    element: <CompetitionRulesPage />,
  },
  {
    path: '/dashboard',
    element: <DashboardWrapper />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <LabPage />,
      },
      {
        path: 'lab',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'notebook/:notebookId',
        element: <NativeNotebookPage />,
      },
      {
        path: 'compute',
        element: <ComputePage />,
      },
      {
        path: 'notebooks',
        element: <NotebooksPage />,
      },
      {
        path: 'data',
        element: <DataPage />,
      },
      {
        path: 'storage',
        element: <StoragePage />,
      },
      {
        path: 'sessions',
        element: <SessionsPage />,
      },
      {
        path: 'strategies',
        element: <StrategiesPage />,
      },
      {
        path: 'strategies/:strategyId',
        element: <StrategyDetailPage />,
      },
      {
        path: 'strategy-library',
        element: <StrategyLibraryPage />,
      },
      {
        path: 'research-feed',
        element: <ResearchFeedPage />,
      },
      {
        path: 'signal-feeds',
        element: <SignalFeedsPage />,
      },
      {
        path: 'strategy-composer',
        element: <StrategyComposerPage />,
      },
      {
        path: 'competitions',
        element: <CompetitionsPage />,
      },
      {
        path: 'competitions/:competitionId',
        element: <CompetitionDetailPage />,
      },
      {
        path: 'leaderboard',
        element: <LeaderboardPage />,
      },
      {
        path: 'billing',
        element: <BillingPage />,
      },
      {
        path: 'api-keys',
        element: <ApiKeysPage />,
      },
      {
        path: 'settings',
        element: <SettingsPage />,
      },
      {
        path: 'test-codemirror',
        element: <TestCodeMirror />,
      },
    ],
  },
  {
    path: '/terminal-window',
    element: <StandaloneTerminalPage />,
  },
  {
    path: '/proxy-window',
    element: <StandaloneProxyPage />,
  },
  {
    path: '/admin',
    element: <DashboardWrapper />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: 'dashboard',
        element: <AdminDashboardPage />,
      },
      {
        path: 'users',
        element: <AdminUsersPage />,
      },
      {
        path: 'users/:userId',
        element: <AdminUserDetailsPage />,
      },
      {
        path: 'gpu-quotas',
        element: <AdminGpuQuotaDashboard />,
      },
      {
        path: 'ami-builder',
        element: <AdminAMIBuilderPage />,
      },
      {
        path: 'cross-cloud-costs',
        element: <CrossCloudCostMonitorPage />,
      },
      {
        path: 'orphan-instances',
        element: <AdminOrphanInstancesPage />,
      },
      {
        path: 'sample-notebooks',
        element: <AdminSampleNotebooksPage />,
      },
      {
        path: 'datasets',
        element: <AdminDatasetsPage />,
      },
      {
        path: 'research',
        element: <AdminResearchPage />,
      },
    ],
  },
]);

function App() {
  return (
    <div className="App">
      <RouterProvider router={router} />
    </div>
  );
}

export default App;